import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  Renderer2,
} from "@angular/core";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { AppPullRefreshService } from "@bitwarden/web-vault/app/services/app-pull-refresh.service";

/**
 * Pull-to-refresh on a scroll container (typically main app shell).
 * Only engages when scrolled to top and user pulls down past threshold.
 * Nested scroll areas (e.g. account details) must also be at scroll top so
 * scrolling up inside them does not trigger a shell pull-to-refresh.
 */
@Directive({
  standalone: true,
  selector: "[appPullToRefresh]",
})
export class PullToRefreshDirective implements AfterViewInit, OnDestroy {
  @Input() appPullToRefreshEnabled = true;
  @Input() appPullToRefreshDisabled = false;
  @Input() appPullToRefreshThreshold = 96;
  @Input() appPullToRefreshCooldownMs = 8000;

  private el = inject(ElementRef<HTMLElement>);
  private renderer = inject(Renderer2);
  private pullRefresh = inject(AppPullRefreshService);
  private i18n = inject(I18nService);

  private startY = 0;
  private active = false;
  private pullDistance = 0;
  private refreshing = false;
  private lastRefreshAt = 0;
  private indicator: HTMLDivElement | null = null;
  /** Main column under the host (router outlet + pages); shifted on pull so body moves with the gesture. */
  private contentRoot: HTMLElement | null = null;
  private removeListeners: Array<() => void> = [];

  ngAfterViewInit(): void {
    const host = this.el.nativeElement;
    this.renderer.addClass(host, "tw-relative");

    this.indicator = this.renderer.createElement("div") as HTMLDivElement;
    this.renderer.addClass(this.indicator, "ptr-pull-indicator");
    /** Overlay above route content (account details etc.); sticky was behind nested stacking contexts. */
    this.renderer.setStyle(this.indicator, "position", "absolute");
    this.renderer.setStyle(this.indicator, "top", "0");
    this.renderer.setStyle(this.indicator, "left", "0");
    this.renderer.setStyle(this.indicator, "right", "0");
    this.renderer.setStyle(this.indicator, "width", "100%");
    this.renderer.setStyle(this.indicator, "z-index", "60");
    this.renderer.setStyle(this.indicator, "display", "flex");
    this.renderer.setStyle(this.indicator, "align-items", "center");
    this.renderer.setStyle(this.indicator, "justify-content", "center");
    this.renderer.setStyle(this.indicator, "min-height", "0");
    this.renderer.setStyle(this.indicator, "overflow", "hidden");
    this.renderer.setStyle(this.indicator, "pointer-events", "none");
    this.renderer.setStyle(this.indicator, "color", "#5C5C5C");
    this.renderer.setStyle(this.indicator, "font-size", "13px");
    this.renderer.setProperty(this.indicator, "textContent", "");
    this.renderer.insertBefore(host, this.indicator, host.firstChild);

    if (host.children.length > 1 && host.children[1] instanceof HTMLElement) {
      this.contentRoot = host.children[1] as HTMLElement;
    }

    const onTouchStart = (e: TouchEvent) => this.handleTouchStart(e);
    const onTouchMove = (e: TouchEvent) => this.handleTouchMove(e);
    const onTouchEnd = (): void => {
      void this.handleTouchEnd();
    };

    host.addEventListener("touchstart", onTouchStart, { passive: true });
    host.addEventListener("touchmove", onTouchMove, { passive: false });
    host.addEventListener("touchend", onTouchEnd);
    host.addEventListener("touchcancel", onTouchEnd);

    this.removeListeners.push(
      () => host.removeEventListener("touchstart", onTouchStart),
      () => host.removeEventListener("touchmove", onTouchMove),
      () => host.removeEventListener("touchend", onTouchEnd),
      () => host.removeEventListener("touchcancel", onTouchEnd),
    );
  }

  ngOnDestroy(): void {
    this.removeListeners.forEach((fn) => fn());
    this.removeListeners = [];
  }

  private handleTouchStart(e: TouchEvent): void {
    if (!this.canPull()) {
      return;
    }
    const host = this.el.nativeElement;
    if (host.scrollTop > 2) {
      this.active = false;
      return;
    }
    if (host.hasAttribute("data-scroll-locked")) {
      this.active = false;
      return;
    }
    if (this.hasNestedScrollableNotAtTop(e.target)) {
      this.active = false;
      return;
    }
    this.active = true;
    this.startY = e.touches[0].clientY;
    this.pullDistance = 0;
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.active || !this.canPull()) {
      return;
    }
    const host = this.el.nativeElement;
    if (host.scrollTop > 2) {
      this.resetPullUi();
      this.active = false;
      return;
    }
    if (host.hasAttribute("data-scroll-locked")) {
      this.resetPullUi();
      this.active = false;
      return;
    }
    if (this.hasNestedScrollableNotAtTop(e.target)) {
      this.resetPullUi();
      this.active = false;
      return;
    }

    const y = e.touches[0].clientY;
    const dy = y - this.startY;
    if (dy > 0) {
      e.preventDefault();
      this.pullDistance = Math.min(dy * 0.55, 120);
      this.updateIndicator();
    } else {
      this.pullDistance = 0;
      this.updateIndicator();
    }
  }

  private async handleTouchEnd(): Promise<void> {
    if (!this.active) {
      return;
    }
    this.active = false;

    const shouldRefresh =
      this.canPull() &&
      this.pullDistance >= this.appPullToRefreshThreshold &&
      Date.now() - this.lastRefreshAt >= this.appPullToRefreshCooldownMs;

    this.resetPullUi();

    if (!shouldRefresh || this.refreshing) {
      return;
    }

    this.refreshing = true;
    this.lastRefreshAt = Date.now();
    try {
      await this.pullRefresh.refresh();
    } finally {
      this.refreshing = false;
    }
  }

  private canPull(): boolean {
    return this.appPullToRefreshEnabled && !this.appPullToRefreshDisabled && !this.refreshing;
  }

  /**
   * True if any scrollable ancestor strictly inside the host (not the host itself)
   * is scrolled away from the top — user is interacting with nested content.
   */
  private hasNestedScrollableNotAtTop(touchTarget: EventTarget | null): boolean {
    const host = this.el.nativeElement;
    let el = this.elementFromTouchTarget(touchTarget);
    while (el && el !== host) {
      if (this.isVerticallyScrollable(el) && el.scrollTop > 2) {
        return true;
      }
      el = el.parentElement;
    }
    return false;
  }

  private elementFromTouchTarget(touchTarget: EventTarget | null): Element | null {
    if (!touchTarget) {
      return null;
    }
    if (touchTarget instanceof Element) {
      return touchTarget;
    }
    if (touchTarget instanceof Text) {
      return touchTarget.parentElement;
    }
    return null;
  }

  private isVerticallyScrollable(el: Element): el is HTMLElement {
    if (!(el instanceof HTMLElement)) {
      return false;
    }
    const style = typeof getComputedStyle !== "undefined" ? getComputedStyle(el) : null;
    if (!style) {
      return false;
    }
    const oy = style.overflowY;
    if (oy !== "auto" && oy !== "scroll" && oy !== "overlay") {
      return false;
    }
    return el.scrollHeight > el.clientHeight + 1;
  }

  private updateIndicator(): void {
    if (!this.indicator) {
      return;
    }
    const h = Math.round(this.pullDistance);
    this.renderer.setStyle(this.indicator, "min-height", `${h}px`);
    if (this.contentRoot) {
      this.renderer.setStyle(
        this.contentRoot,
        "transform",
        h > 0 ? `translate3d(0, ${h}px, 0)` : "none",
      );
    }
    if (h > this.appPullToRefreshThreshold * 0.5) {
      this.renderer.setProperty(
        this.indicator,
        "textContent",
        h >= this.appPullToRefreshThreshold
          ? this.i18n.t("releaseToRefresh")
          : this.i18n.t("pullToRefresh"),
      );
    } else {
      this.renderer.setProperty(this.indicator, "textContent", "");
    }
  }

  private resetPullUi(): void {
    this.pullDistance = 0;
    if (this.indicator) {
      this.renderer.setStyle(this.indicator, "min-height", "0");
      this.renderer.setProperty(this.indicator, "textContent", "");
    }
    if (this.contentRoot) {
      this.renderer.removeStyle(this.contentRoot, "transform");
    }
  }
}
